import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getPlanners, deletePlanner, duplicatePlanner } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, MoreHorizontal, Pencil, Trash2, Copy, ArrowLeft, Search, Loader2, BarChart3, Eye } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../lib/auth';
import { format, parseISO } from 'date-fns';

export default function PlannerListPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasRole } = useAuth();
  const canCreate = hasRole(['admin', 'project_manager']);
  const [planners, setPlanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();
  const kindFilter = searchParams.get('kind'); // 'marketing_commerciale' | 'recruitment_sales' | null

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await getPlanners();
      setPlanners(res.data.data);
    } catch {
      toast({ title: 'Errore', description: 'Impossibile caricare i planner', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo planner?')) return;
    try {
      await deletePlanner(id);
      toast({ title: 'Planner eliminato' });
      loadData();
    } catch {
      toast({ title: 'Errore', description: 'Impossibile eliminare', variant: 'destructive' });
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const res = await duplicatePlanner(id);
      toast({ title: 'Planner duplicato' });
      const dest = res.data.kind === 'recruitment_sales'
        ? `/toolkit/recruitment-planner/${res.data.id}`
        : `/toolkit/planner/${res.data.id}`;
      navigate(dest);
    } catch {
      toast({ title: 'Errore', description: 'Impossibile duplicare', variant: 'destructive' });
    }
  };

  const filtered = useMemo(() => {
    let list = planners;
    if (kindFilter) {
      list = list.filter(p => (p.kind || 'marketing_commerciale') === kindFilter);
    }
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.client_name || '').toLowerCase().includes(q)
    );
  }, [planners, search, kindFilter]);

  const kindLabel = kindFilter === 'recruitment_sales'
    ? 'Planner Reclutamento & Vendita'
    : kindFilter === 'marketing_commerciale'
      ? 'Planner Marketing & Commerciale'
      : 'Planner Previsionali';

  const editorPathFor = (p) => (p.kind === 'recruitment_sales' ? `/toolkit/recruitment-planner/${p.id}` : `/toolkit/planner/${p.id}`);
  const newPath = kindFilter === 'recruitment_sales' ? '/toolkit/recruitment-planner/new' : '/toolkit/planner/new';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="planner-list-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/toolkit')} data-testid="back-to-toolkit">
            <ArrowLeft className="w-4 h-4 mr-1" /> Toolkit
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{kindLabel}</h1>
            <p className="text-sm text-gray-500">{filtered.length} planner</p>
          </div>
        </div>
        {canCreate && (
          <Button onClick={() => navigate(newPath)} data-testid="new-planner-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuovo Planner
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Cerca per nome o cliente..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="planner-search"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="px-4 py-0">
          {filtered.length === 0 ? (
            <div className="text-center py-12">
              <BarChart3 className="mx-auto h-12 w-12 text-gray-300" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {search ? 'Nessun risultato' : 'Nessun planner creato'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {search ? 'Prova con un termine diverso.' : 'Crea il tuo primo planner previsionale.'}
              </p>
              {!search && canCreate && (
                <Button className="mt-4" onClick={() => navigate(newPath)}>
                  <Plus className="w-4 h-4 mr-2" /> Nuovo Planner
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Creato il</TableHead>
                  <TableHead>Ultima modifica</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow
                    key={p.id}
                    className="cursor-pointer hover:bg-orange-50/50"
                    onClick={() => navigate(editorPathFor(p))}
                    data-testid={`planner-row-${p.id}`}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {p.name}
                        {p.kind === 'recruitment_sales' && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded">Reclutamento</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{p.client_name || <span className="text-gray-400">-</span>}</TableCell>
                    <TableCell>{p.created_at ? format(parseISO(p.created_at), 'dd/MM/yyyy') : '-'}</TableCell>
                    <TableCell>{p.updated_at ? format(parseISO(p.updated_at), 'dd/MM/yyyy HH:mm') : '-'}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(editorPathFor(p)); }}>
                            {canCreate ? <Pencil className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                            {canCreate ? 'Modifica' : 'Visualizza'}
                          </DropdownMenuItem>
                          {canCreate && (
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicate(p.id); }}>
                              <Copy className="w-4 h-4 mr-2" /> Duplica
                            </DropdownMenuItem>
                          )}
                          {canCreate && (
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" /> Elimina
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
